/**
 * Common error types
 * @module @orchard9ai/types/common/errors
 */

/**
 * Base error interface
 */
export interface BaseError {
  /** Error name/type */
  name: string;
  /** Error message */
  message: string;
  /** Optional error code */
  code?: string;
  /** Stack trace */
  stack?: string;
  /** Additional error context */
  context?: Record<string, unknown>;
}

/**
 * Application error codes
 */
export enum ErrorCode {
  // Authentication errors
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',

  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  INVALID_FORMAT = 'INVALID_FORMAT',

  // Resource errors
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  CONFLICT = 'CONFLICT',
  GONE = 'GONE',

  // Rate limiting
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',

  // Server errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  TIMEOUT = 'TIMEOUT',

  // Business logic errors
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  INVALID_OPERATION = 'INVALID_OPERATION',
  PRECONDITION_FAILED = 'PRECONDITION_FAILED',
}

/**
 * HTTP error with status code
 */
export interface HttpError extends BaseError {
  /** HTTP status code */
  statusCode: number;
  /** Request URL */
  url?: string;
  /** Request method */
  method?: string;
}

/**
 * Validation error with field details
 */
export interface ValidationError extends BaseError {
  /** Field-specific errors */
  fields: Record<string, string[]>;
  /** Validation rules that failed */
  rules?: Record<string, any>;
}

/**
 * Business logic error
 */
export interface BusinessError extends BaseError {
  /** Business rule that was violated */
  rule?: string;
  /** Suggested action to resolve */
  suggestion?: string;
}

/**
 * Network error
 */
export interface NetworkError extends BaseError {
  /** Network error code */
  networkCode?: string;
  /** Was the request retried */
  retried?: boolean;
  /** Number of retry attempts */
  retryCount?: number;
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  /** Low severity - can be ignored */
  Low = 'LOW',
  /** Medium severity - should be logged */
  Medium = 'MEDIUM',
  /** High severity - requires attention */
  High = 'HIGH',
  /** Critical severity - immediate action required */
  Critical = 'CRITICAL',
}

/**
 * Error with severity and metadata
 */
export interface ApplicationError extends BaseError {
  /** Error code from ErrorCode enum */
  code: ErrorCode;
  /** Error severity */
  severity: ErrorSeverity;
  /** ISO timestamp when error occurred */
  timestamp: string;
  /** User ID if authenticated */
  userId?: string;
  /** Session ID */
  sessionId?: string;
  /** Request ID for tracing */
  requestId?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Error boundary fallback props
 */
export interface ErrorBoundaryState {
  /** Has error occurred */
  hasError: boolean;
  /** The error that was caught */
  error?: Error;
  /** Error info from React */
  errorInfo?: {
    componentStack: string;
  };
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  /** Maximum number of retries */
  maxRetries: number;
  /** Initial retry delay in ms */
  initialDelay: number;
  /** Maximum retry delay in ms */
  maxDelay: number;
  /** Backoff multiplier */
  backoffMultiplier: number;
  /** Errors that should trigger retry */
  retryableErrors?: ErrorCode[];
}
